import Vue from 'vue'
import Vuex from 'vuex'

import moment from 'moment'
import policies from '../api/policies'
import invoices from '../api/invoices'
import payroll from '../api/payroll'
import authentication from '../api/authentication'
import { getAccessToken, setAccessToken } from '@/utils/auth'
import { store, retrieve } from '@/utils/storage'

import stateAbbreviationToFullName from './stateAbbreviationToFullName';

Vue.use(Vuex)

const FETCH_POLICIES               = 'FETCH_POLICIES'
const FETCH_INVOICES               = 'FETCH_INVOICES'
const SET_CONTACT                  = 'SET_CONTACT'
const SET_REPORTING                = 'SET_REPORTING'
const SET_SUBMITTED_PAYROLL        = 'SET_SUBMITTED_PAYROLL'
const INCREMENT_ASYNC_COUNT        = 'INCREMENT_ASYNC_COUNT'
const DECREMENT_ASYNC_COUNT        = 'DECREMENT_ASYNC_COUNT'
const HAS_REPORTED                 = 'HAS_REPORTED'
const RESET                        = 'RESET'
const SET_OVERLAY_MESSAGE          = 'SET_OVERLAY_MESSAGE'
const LOADED_HISTORY               = 'LOADED_HISTORY'
const UNLOADED_HISTORY             = 'UNLOADED_HISTORY'

function initialState () {
    return {
        policies: retrieve('policies') || [],
        invoices: retrieve('invoices') || [],
        loadedHistory: false,
        submittedPayroll: retrieve('submittedPayroll') || [],
        contact: retrieve('contact') || {},
        asyncCount: 0,
        stateAbbreviationToFullName: stateAbbreviationToFullName,
        reporting: retrieve('reporting') || {},
        overlayMessage: "Loading"
    }
}

export default new Vuex.Store({
    state: initialState(),
    mutations: {
        [FETCH_POLICIES]: (state, newPolicies) => {
            state.policies = newPolicies
        },
        [FETCH_INVOICES]: (state, newInvoices) => {
            state.invoices = newInvoices
        },
        [SET_CONTACT]: (state, contact) => {
            state.contact = contact
        },
        [SET_REPORTING]: (state, reporting) => {
            state.reporting = reporting
        },
        [SET_SUBMITTED_PAYROLL]: (state, submittedPayroll) => {
            state.submittedPayroll = submittedPayroll
        },
        [INCREMENT_ASYNC_COUNT]: (state) => {
            state.asyncCount++
        },
        [DECREMENT_ASYNC_COUNT]: (state) => {
            state.asyncCount--
        },
        [HAS_REPORTED]: (state, hasReportedForMonth) => {
            state.contact.hasReported = hasReportedForMonth
        },
        [RESET]: (state) => {
            const s = initialState()
            Object.keys(s).forEach(key => {
                state[key] = s[key]
            })
        },
        [SET_OVERLAY_MESSAGE]: (state, message) => {
            state.overlayMessage = message
        },
        [LOADED_HISTORY]: (state) => {
            state.loadedHistory = true
        },
        [UNLOADED_HISTORY]: (state) => {
            state.loadedHistory = false
        }
    },
    getters: {
        isLoading: (state) => {
            return state.asyncCount > 0
        },
        getOverlayMessage: (state) => {
            return state.overlayMessage
        }
    },
    actions: {
        login: ({ commit }, { username, password, isAdmin }) => {
            commit(INCREMENT_ASYNC_COUNT)
            commit(SET_OVERLAY_MESSAGE, "Logging in")
            return new Promise((resolve, reject) => {
                authentication.post('', 'username=' + username + '&password=' + password + '&isAdmin=' + isAdmin)
                    .then((response) => {
                        setAccessToken(response.data.authToken)
                        store('contact', response.data.contact)
                        commit(SET_CONTACT, response.data.contact)
                        return resolve()
                    })
                    .catch(error => {
                        Vue.prototype.$log.error(error)
                        if (error.response.status === 403) {
                            error.response.data.message = 'An invalid username or password was provided.'
                        }
                        if (error.response.data.message === undefined) {
                            error.response.data.message = 'Unable to retrieve account information. Please contact your Account Manager.';
                        }
                        return reject(error.response.data)
                    })
                    .finally(() => {
                        commit(DECREMENT_ASYNC_COUNT)
                    })
            })
        },
        fetchPolicies: ({ commit }, { reportingDate }) => {
            commit(INCREMENT_ASYNC_COUNT)
            return new Promise((resolve, reject) => {
                let url = '?compressLocationsByState=true' + (reportingDate ? '&reportingDate=' + reportingDate.format('YYYY-MM-DD') : '')
                policies
                    .get(url, { headers: { 'Authorization': `Bearer ${getAccessToken()}` } })
                    .then(response => {
                        store('policies', response.data.policies)
                        commit(FETCH_POLICIES, response.data.policies)
                        const actualReportingDate = reportingDate == null ? moment(response.data.serverReportingDateMillis).startOf('day') : reportingDate
                        let reporting = {
                            date: moment(response.data.serverReportingDateMillis),
                            payrollPeriodStart: response.data.reportPeriodStart,
                            payrollPeriodEnd: response.data.reportPeriodEnd,
                            startDate: moment(response.data.canReportStartingMillis),
                            endDate: moment(response.data.cutoffWithGracePeriodMillis),
                            reportingDate: actualReportingDate
                        }
                        store('reporting', reporting)
                        commit(SET_REPORTING, reporting)
                        resolve()
                    })
                    .catch(error => {
                        Vue.prototype.$log.error(error.response.data.message)
                        if (error.response.status === 400) {
                            if (error.response.data.errorMsg === 'NO_ACTIVE_POLICY') {
                                error.response.data.message = 'A currently active policy with Payroll Self-Reporting services was not found.';
                            } else if (error.response.data.errorMsg === 'NO_REPORTING_POLICY') {
                                error.response.data.message = 'Thank you for selecting Omaha National! Your policy is not yet eligible for Payroll Self-Reporting. Please log back in on or after the 20th of the month to submit your first report.';
                            } else if (error.response.data.message === undefined) {
                                error.response.data.message = 'Unable to retrieve policy information. Please contact your Account Manager.';
                            }
                        }
                        return reject(error.response.data)
                    })
                    .finally(() => {
                        commit(DECREMENT_ASYNC_COUNT)
                    })
            })

        },
        fetchInvoices: ({ commit }) => {
            commit(INCREMENT_ASYNC_COUNT)
            return new Promise((resolve, reject) => {
                invoices
                    .get('', { headers: { 'Authorization': `Bearer ${getAccessToken()}` } })
                    .then(response => {
                        commit(FETCH_INVOICES, response.data.list)
                        store('invoices', response.data.list)
                        resolve()
                    })
                    .catch(error => {
                        if (error.response.data.errorMsg !== undefined) {
                            error.response.data.message = error.response.data.errorMsg;
                        } else {
                            error.response.data.message = 'Unable to retrieve invoice information.';
                        }
                        Vue.prototype.$log.error(error)
                        return reject(error.response.data)
                    })
                    .finally(() => {
                        commit(DECREMENT_ASYNC_COUNT)
                        commit(LOADED_HISTORY)
                    })
            })
        },
        submitPayroll: ({ commit, state }, submissions) => {
            commit(INCREMENT_ASYNC_COUNT)
            commit(SET_OVERLAY_MESSAGE, "Sending reports")
            return new Promise((resolve, reject) => {
                payroll
                    .post('', { submissions }, { headers: { 'Authorization': `Bearer ${getAccessToken()}` } })
                    .then(() => {
                        store('submittedPayroll', submissions)
                        commit(SET_SUBMITTED_PAYROLL, submissions)
                        store('contact', state.contact)
                        commit(HAS_REPORTED, true)
                        commit(UNLOADED_HISTORY)
                        resolve()
                    })
                    .catch(error => {
                        if (error.response.status === 400) {
                            if (error.response.data.errorMsg !== undefined) {
                                error.response.data.message = 'Invoice submission failed: ' + error.response.data.errorMsg
                            } else {
                                error.response.data.message = 'Invoice submission failed'
                            }
                        }
                        Vue.prototype.$log.error(error)
                        return reject(error.response.data)
                    })
                    .finally(() => {
                        commit(DECREMENT_ASYNC_COUNT)
                    })
            })
        },
        reset: ({ commit }) => {
            commit(RESET)
        },
        setOverlayMessage: ({commit}, message) => {
            commit(SET_OVERLAY_MESSAGE, message)
        }
    }
})
